import { Frame as FrameIcon, Layers } from "lucide-react";
import { useMoney } from "@/context/CurrencyContext";
import { swatchClass } from "./ArtMockup";

const optCls = (on) => `px-3 py-3 border text-left transition ${on ? "border-ink bg-ink text-[color:var(--brand-cream)]" : "border-ink-20 hover:border-ink"}`;
const rowCls = (on) => `w-full flex items-center justify-between px-5 py-4 border transition ${on ? "border-ink bg-ink text-[color:var(--brand-cream)]" : "border-ink-20 hover:border-ink"}`;

export const MaterialPicker = ({ materials, material, onChange, meta }) => {
  const money = useMoney();
  if (materials.length <= 1) return null;
  return (
    <div className="mb-8">
      <div className="overline text-mute mb-4 inline-flex items-center gap-2"><Layers size={12}/> Print Material</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {materials.map(m => (
          <button key={m.material} onClick={() => onChange(m.material)} data-testid={`material-option-${m.material}`} className={optCls(material === m.material)}>
            <div className="text-xs font-medium">{m.label}</div>
            <div className="text-[10px] opacity-70 mt-1">{m.starts_at_usd ? `From ${money(m.starts_at_usd)}` : "—"}</div>
          </button>
        ))}
      </div>
      {meta?.description && <p className="text-[11px] text-mute mt-3 leading-relaxed" data-testid="material-description">{meta.description}</p>}
    </div>
  );
};

export const SizePicker = ({ art, material, size, onSize, materialSizes, materialSizeSel, onMaterialSize }) => {
  const money = useMoney();
  const poster = material === "poster";
  return (
    <div className="mb-8">
      <div className="overline text-mute mb-4">Available Sizes</div>
      <div className="space-y-2">
        {poster
          ? (art.sizes || []).map(s => (
            <button key={s.size} onClick={() => onSize(s)} data-testid={`size-option-${s.size}`} className={rowCls(size?.size === s.size)}>
              <span className="font-medium">{s.size}</span><span>{money(s.price)}</span>
            </button>
          ))
          : materialSizes.map(s => (
            <button key={s.size} onClick={() => onMaterialSize(s)} data-testid={`material-size-option-${material}-${s.size}`} className={rowCls(materialSizeSel?.size === s.size)}>
              <span className="font-medium">{s.size}</span><span>{money(s.retail_price_usd)}</span>
            </button>
          ))}
        {poster && (art.sizes || []).length === 0 && <p className="text-sm text-mute">Not currently available. Please contact the studio.</p>}
        {!poster && materialSizes.length === 0 && <p className="text-sm text-mute" data-testid="material-sizes-empty">Loading sizes…</p>}
      </div>
      {art.prints_only && (
        <p className="text-[11px] text-mute mt-3 leading-relaxed" data-testid="listed-base-price-note">
          Listed Base Price — final price including shipping will be displayed at checkout.
        </p>
      )}
    </div>
  );
};

export const FramePicker = ({ frames, frame, onChange }) => {
  const money = useMoney();
  if (frames.length === 0) return null;
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="overline text-mute inline-flex items-center gap-2"><FrameIcon size={12}/> Framing</div>
        <span className="text-xs text-mute">Museum-quality wood, plexiglass</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <button onClick={() => onChange(null)} data-testid="frame-option-none" className={optCls(!frame)}>
          <div className="text-xs font-medium">Unframed</div>
          <div className="text-[10px] opacity-70 mt-1">Rolled print</div>
        </button>
        {frames.map(opt => (
          <button key={opt.frame_color} onClick={() => onChange({ frame_color: opt.frame_color, upsell_usd: opt.upsell_usd })}
            data-testid={`frame-option-${opt.frame_color}`} className={optCls(frame?.frame_color === opt.frame_color)}>
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-block w-3 h-3 border ${swatchClass(opt.frame_color)}`}></span>
              <div className="text-xs font-medium">{opt.label}</div>
            </div>
            <div className="text-[10px] opacity-70">+{money(opt.upsell_usd)}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

// Frame colour for framed materials (framed canvas, framed/mounted giclée) — the frame is part of the product, no "unframed".
export const MaterialFrameColorPicker = ({ options, value, onChange }) => {
  const money = useMoney();
  if (!options.length) return null;
  return (
    <div className="mb-8" data-testid="material-frame-colors">
      <div className="overline text-mute mb-4 inline-flex items-center gap-2"><FrameIcon size={12}/> Frame Colour</div>
      <div className="grid grid-cols-3 gap-2">
        {options.map(opt => (
          <button key={opt.frame_color} onClick={() => onChange(opt.frame_color)} data-testid={`material-frame-${opt.frame_color}`} className={optCls(value === opt.frame_color)}>
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-block w-3 h-3 border ${swatchClass(opt.frame_color)}`}></span>
              <div className="text-xs font-medium">{opt.label}</div>
            </div>
            <div className="text-[10px] opacity-70">{money(opt.retail_price_usd)}</div>
          </button>
        ))}
      </div>
    </div>
  );
};
